import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFilters } from '../api';
import Lottie from 'lottie-react';
import { motion, AnimatePresence } from 'framer-motion';
import { trackEvent } from "../utils/analytics";

import syllabusAnim from '../animations/syllabus.json';
import gradeAnim from '../animations/grade.json';
import subjectAnim from '../animations/subject.json';
import calendarAnim from '../animations/calender.json';
import termAnim from '../animations/term.json';
import universityAnim from '../animations/school_building.json';

function QuestionSelector() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const category = params.get("category") || "school";
  const qParam = params.get("q") || "";
  const teaserBoard = params.get("board") || "";
  const teaserGrade = params.get("grade") || "";
  const teaserSubject = params.get("subject") || "";

  const [step, setStep] = useState(1);
  const [filters, setFilters] = useState({});
  const [selected, setSelected] = useState({
    program: '', board: '', stream: '', grade: '',
    subject: '', mode: '', year: '', term: '', month: ''
  });
  const [loading, setLoading] = useState(true);

  const config = category === "school"
    ? { steps: ['board', 'grade', 'subject', 'year', 'term'], labels: ['Select your Board', 'Choose Grade', 'Pick Subject', 'Choose Year', 'Select Term'], animations: [syllabusAnim, gradeAnim, subjectAnim, calendarAnim, termAnim] }
    : { steps: ['program', 'board', 'stream', 'grade', 'subject', 'year', 'month'], labels: ['Select Program', 'Select University', 'Select Stream', 'Choose Semester', 'Pick Subject', 'Choose Year', 'Select Exam Month'], animations: [syllabusAnim, universityAnim, universityAnim, gradeAnim, subjectAnim, calendarAnim, termAnim] };

  const currentKey = config.steps[step - 1] || config.steps[0];
  const keyMap = { month: "months" };
  const frequentOption = { value: 'frequent', label: '🔥 Most Repeated Questions', isSpecial: true };

  useEffect(() => { trackEvent("selector_step_view", { category, step, step_key: currentKey }); }, [category, step, currentKey]);

  useEffect(() => {
    const fetchFilters = async () => {
      setLoading(true);
      try {
        const clean = (val) => typeof val === "object" ? val?.name || val?.value || "" : val;
        const res = await getFilters({ category, program: clean(selected.program), board: clean(selected.board), stream: clean(selected.stream), grade: clean(selected.grade), subject: clean(selected.subject), year: clean(selected.year) });
        if (!res) return;
        const apiKey = keyMap[currentKey] || currentKey + "s";
        setFilters(prev => ({ ...prev, [apiKey]: res[apiKey] || [] }));
      } catch (err) { console.error("Error:", err); } finally { setLoading(false); }
    };
    fetchFilters();
  }, [step, selected, category]);

  useEffect(() => {
    if (qParam && teaserBoard && !loading && filters.boards?.length) {
      const boardData = filters.boards.find(b => b.name === teaserBoard) || filters.boards[0];
      const gradeData = filters.grades?.find(g => g.grade_number === parseInt(teaserGrade)) || filters.grades?.[0];
      const subjectData = filters.subjects?.find(s => s.name.toLowerCase() === teaserSubject.toLowerCase()) || filters.subjects?.[0];
      if (boardData && gradeData && subjectData) {
        trackEvent("teaser_navigation", { question: qParam });
        navigate(`/questions?${new URLSearchParams({ category, board: boardData.name, grade: gradeData.grade_number, subject: subjectData.name, mode: 'frequent', q: qParam }).toString()}`);
      }
    }
  }, [qParam, teaserBoard, teaserGrade, teaserSubject, loading, filters, category, navigate]);

  const baseOptions = filters[keyMap[currentKey] || currentKey + "s"] || [];
  const options = currentKey === 'year' ? [...baseOptions, frequentOption] : baseOptions;

  const handleSelect = (value) => {
    const cleanValue = typeof value === "object" ? value.value : value;
    const isFrequentOption = typeof value === "object" && value.isSpecial;

    const stepEventMap = {
      board: "board_selected",
      grade: "grade_selected",
      subject: "subject_selected",
      year: "year_selected",
      term: "term_selected",
      program: "program_selected",
      stream: "stream_selected",
      month: "month_selected"
    };
    const stepEvent = stepEventMap[currentKey];
    if (stepEvent) {
      trackEvent(stepEvent, {
        category,
        board: selected.board || cleanValue,
        grade: selected.grade,
        subject: selected.subject,
        value: cleanValue
      });
    }

    trackEvent("selector_option_selected", { category, step, step_key: currentKey, value: cleanValue });
    if (isFrequentOption || cleanValue === 'frequent') {
      trackEvent("final_selection", { category, ...selected, mode: 'frequent' });
      navigate(`/questions?${new URLSearchParams({ category, ...selected, mode: 'frequent' }).toString()}`);
      return;
    }
    const newSelected = { ...selected, [currentKey]: cleanValue };
    setSelected(newSelected);
    if (step < config.steps.length) { setStep(step + 1); }
    else { trackEvent("final_selection", { category, ...newSelected }); navigate(`/questions?${new URLSearchParams({ category, ...newSelected }).toString()}`); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fc', padding: '40px 20px' }}>
      <div style={{ maxWidth: '500px', margin: '0 auto 30px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        {config.steps.map((_, i) => (
          <div key={i} style={{ flex: 1, height: '4px', borderRadius: '2px' }}>
            <div style={{ height: '100%', borderRadius: '2px', background: i < step ? 'linear-gradient(90deg, #6366f1, #8b5cf6)' : '#e5e7eb', transition: 'background 0.3s ease' }} />
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -30, scale: 0.95 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          style={{
            maxWidth: '600px', margin: '0 auto', background: '#ffffff',
            backdropFilter: 'blur(20px)', borderRadius: '24px', padding: '40px 30px',
            border: '1px solid #e5e7eb', boxShadow: '0 12px 40px rgba(0,0,0,0.06)', textAlign: 'center'
          }}
        >
          <Lottie animationData={config.animations[step - 1]} style={{ height: 160, marginBottom: '20px' }} loop />
          <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#111827', marginBottom: '30px' }}>{config.labels[step - 1]}</h2>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
            {loading ? (
              <div style={{ padding: '30px', color: '#6b7280', fontSize: '15px' }}>Loading options...</div>
            ) : options.length === 0 ? (
              <div style={{ padding: '30px', color: '#9ca3af', fontSize: '15px' }}>No options available</div>
            ) : (
              options.map((opt, i) => {
                const isSpecial = typeof opt === "object" && opt.isSpecial;
                return (
                  <motion.button
                    key={`${opt}-${i}`}
                    onClick={() => handleSelect(opt)}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    style={{
                      padding: '14px 24px', borderRadius: '12px', fontSize: '15px', fontWeight: 600,
                      background: isSpecial ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                      color: 'white', border: 'none', cursor: 'pointer',
                      boxShadow: isSpecial ? '0 4px 15px rgba(249,115,22,0.3)' : '0 4px 15px rgba(99,102,241,0.3)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {typeof opt === "object" ? opt.label : opt}
                  </motion.button>
                );
              })
            )}
          </div>

          {step > 1 && (
            <motion.button
              onClick={() => setStep(step - 1)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{
                marginTop: '30px', padding: '12px 24px', background: '#f3f4f6',
                color: '#6b7280', borderRadius: '10px', border: '1px solid #e5e7eb',
                fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px', margin: '30px auto 0'
              }}
            >
              ← Back
            </motion.button>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default QuestionSelector;
